import type { QueryClient } from '@tanstack/react-query';
import { create } from 'zustand';

import { getErrorMessage } from '@/shared/api/errorMessage';
import { isApiError } from '@/shared/api/errors';
import { forgetPendingUpload, type PendingUpload } from '@/shared/api/pendingUploads';
import { api } from '@/shared/api/http';
import { uploadFile, type UploadStatus as ConnectionStatus } from '@/shared/api/upload';

import { diskApi, invalidateDisk, type ConflictMode } from '../api';

/*
 * Uploads into disk folders, kept outside React so they continue while the user moves around the portal: each file is
 * uploaded to the files module (`POST /api/files`, or in chunks that are retried and survive a page reload when it is large —
 * shared/api/resumableUpload.ts; progress over XHR) and then added to its folder
 * (`POST /api/disk/folders/{id}/files` with the chosen conflict mode). Three files travel at a time.
 * A folder upload is a batch: the folders were created at once, every file of it is a task of its own (so one that
 * fails is retried alone) and the panel shows the batch as one row.
 */

export interface UploadTarget {
  folderId: string;
  folderName: string;
  storageId: string;
}

export type UploadStatus = 'queued' | 'uploading' | 'saving' | 'done' | 'error';

export interface DiskUploadTask {
  key: string;
  file: File;
  target: UploadTarget;
  conflict: ConflictMode;
  status: UploadStatus;
  progress: number;
  error?: string;
  itemId?: string;
  controller: AbortController;
  /** The folder upload this file belongs to. */
  batch?: string;
  /** Where the file is inside its batch (`photos/2026/a.jpg`). */
  path?: string;
  /** Retrying, waiting for the network or resumed (large files are sent in chunks); absent while simply sending. */
  connection?: ConnectionStatus;
}

/** What the Disk keeps with an unfinished upload (pendingUploads.ts) to continue it after a page reload. */
export interface DiskResumeMeta {
  module: 'disk';
  target: UploadTarget;
  conflict: ConflictMode;
  path?: string;
}

/** The Disk's own part of the unfinished uploads of this browser (the other modules' ones are not listed here). */
export function diskResumeMeta(entry: PendingUpload): DiskResumeMeta | null {
  const meta = entry.meta as Partial<DiskResumeMeta> | undefined;
  const target = meta?.target;
  return meta?.module === 'disk' &&
    typeof target?.folderId === 'string' &&
    typeof target.folderName === 'string' &&
    typeof target.storageId === 'string' &&
    typeof meta.conflict === 'string'
    ? (meta as DiskResumeMeta)
    : null;
}

/** A folder upload as one row of the panel. */
export interface UploadBatch {
  id: string;
  /** The folder(s) that were uploaded. */
  label: string;
  /** The folder they went into. */
  folderName: string;
}

export interface BatchItem {
  file: File;
  target: UploadTarget;
  conflict: ConflictMode;
  path: string;
}

const CONCURRENCY = 3;
let seq = 0;
let queryClient: QueryClient | null = null;

/** The module's query client (set once in `setup`): finished uploads refresh the folder they went to. */
export function setUploadQueryClient(client: QueryClient): void {
  queryClient = client;
}

interface UploadState {
  tasks: DiskUploadTask[];
  batches: UploadBatch[];
  /** The floating panel is shown (it opens when uploads start, the user closes it). */
  open: boolean;
  collapsed: boolean;
  /** The user closed the panel: the unfinished uploads of an earlier page load are not offered again until the next one. */
  interruptedHidden: boolean;
  enqueue: (files: File[], target: UploadTarget, conflictFor: (file: File) => ConflictMode) => void;
  /** Queues the files of a folder upload, each into its own (already created) folder. */
  enqueueBatch: (batch: Pick<UploadBatch, 'label' | 'folderName'>, items: BatchItem[]) => void;
  cancel: (key: string) => void;
  /** Cancels what is left of a folder upload (what already went through stays). */
  cancelBatch: (id: string) => void;
  retry: (key: string) => void;
  /** Retries every failed file of a folder upload. */
  retryFailed: (id: string) => void;
  /** Forgets an unfinished upload of an earlier page load (also removes it at the server). */
  discardInterrupted: (entry: PendingUpload) => void;
  /** Closes the panel; running uploads are cancelled. */
  dismiss: () => void;
  setOpen: (open: boolean) => void;
  setCollapsed: (collapsed: boolean) => void;
}

export const useUploadStore = create<UploadState>()((set, get) => {
  const patch = (key: string, change: Partial<DiskUploadTask>) =>
    set((s) => ({ tasks: s.tasks.map((t) => (t.key === key ? { ...t, ...change } : t)) }));

  const run = async (task: DiskUploadTask) => {
    patch(task.key, { status: 'uploading', progress: 0, error: undefined });
    try {
      const resume: DiskResumeMeta = {
        module: 'disk',
        target: task.target,
        conflict: task.conflict,
        ...(task.path ? { path: task.path } : {}),
      };
      const ref = await uploadFile(task.file, {
        signal: task.controller.signal,
        onProgress: ({ percent }) => patch(task.key, { progress: percent }),
        onStatus: (status) =>
          patch(task.key, { connection: status.kind === 'uploading' ? undefined : status }),
        resume: { meta: { ...resume } },
      });
      if (task.controller.signal.aborted) return;
      patch(task.key, { status: 'saving', progress: 100, connection: undefined });
      const item = await diskApi.addFile(task.target.folderId, ref.id, task.file.name, task.conflict);
      patch(task.key, { status: 'done', itemId: item.id });
      if (queryClient) {
        invalidateDisk(queryClient, {
          storageId: task.target.storageId,
          folderIds: [task.target.folderId],
          itemIds: [item.id],
        });
      }
    } catch (error) {
      if (isApiError(error) && error.isAborted) {
        set((s) => ({ tasks: s.tasks.filter((t) => t.key !== task.key) }));
      } else {
        patch(task.key, { status: 'error', error: getErrorMessage(error) });
      }
    } finally {
      pump();
    }
  };

  const pump = () => {
    const { tasks } = get();
    let running = tasks.filter((t) => t.status === 'uploading' || t.status === 'saving').length;
    for (const task of tasks) {
      if (running >= CONCURRENCY) break;
      if (task.status !== 'queued') continue;
      running++;
      // Marked synchronously so a second pump does not start it again.
      task.status = 'uploading';
      void run(task);
    }
  };

  /** What a new upload leaves of the earlier ones: unfinished work, and the finished files of a folder upload that is not done. */
  const keepAfterNew = (tasks: DiskUploadTask[]) => {
    const unfinished = new Set(tasks.filter((t) => t.batch && t.status !== 'done').map((t) => t.batch));
    return tasks.filter((t) => t.status !== 'done' || (t.batch !== undefined && unfinished.has(t.batch)));
  };

  const withoutEmptyBatches = (batches: UploadBatch[], tasks: DiskUploadTask[]) =>
    batches.filter((b) => tasks.some((t) => t.batch === b.id));

  return {
    tasks: [],
    batches: [],
    open: false,
    collapsed: false,
    interruptedHidden: false,
    enqueue: (files, target, conflictFor) => {
      if (files.length === 0) return;
      const created = files.map<DiskUploadTask>((file) => ({
        key: `disk-upload-${++seq}`,
        file,
        target,
        conflict: conflictFor(file),
        status: 'queued',
        progress: 0,
        controller: new AbortController(),
      }));
      set((s) => {
        // A new upload drops what finished earlier, so the panel shows the current work.
        const tasks = [...keepAfterNew(s.tasks), ...created];
        return {
          tasks,
          batches: withoutEmptyBatches(s.batches, tasks),
          open: true,
          collapsed: false,
          interruptedHidden: false,
        };
      });
      pump();
    },
    enqueueBatch: (info, items) => {
      if (items.length === 0) return;
      const batch: UploadBatch = { id: `disk-batch-${++seq}`, ...info };
      const created = items.map<DiskUploadTask>((item) => ({
        key: `disk-upload-${++seq}`,
        file: item.file,
        target: item.target,
        conflict: item.conflict,
        status: 'queued',
        progress: 0,
        controller: new AbortController(),
        batch: batch.id,
        path: item.path,
      }));
      set((s) => {
        const tasks = [...keepAfterNew(s.tasks), ...created];
        return {
          tasks,
          batches: [...withoutEmptyBatches(s.batches, tasks), batch],
          open: true,
          collapsed: false,
        };
      });
      pump();
    },
    cancel: (key) => {
      const task = get().tasks.find((t) => t.key === key);
      if (!task) return;
      if (task.status === 'uploading' || task.status === 'saving') task.controller.abort();
      set((s) => {
        const tasks = s.tasks.filter((t) => t.key !== key);
        return { tasks, batches: withoutEmptyBatches(s.batches, tasks) };
      });
      pump();
    },
    cancelBatch: (id) => {
      for (const task of get().tasks) {
        if (task.batch === id && (task.status === 'uploading' || task.status === 'saving'))
          task.controller.abort();
      }
      // Finished files stay (they are in the folder); the rest goes.
      set((s) => {
        const tasks = s.tasks.filter((t) => t.batch !== id || t.status === 'done');
        return { tasks, batches: withoutEmptyBatches(s.batches, tasks) };
      });
      pump();
    },
    retry: (key) => {
      set((s) => ({
        tasks: s.tasks.map((t) =>
          t.key === key
            ? {
                ...t,
                status: 'queued',
                progress: 0,
                error: undefined,
                connection: undefined,
                controller: new AbortController(),
              }
            : t,
        ),
      }));
      pump();
    },
    retryFailed: (id) => {
      set((s) => ({
        tasks: s.tasks.map((t) =>
          t.batch === id && t.status === 'error'
            ? { ...t, status: 'queued', progress: 0, error: undefined, controller: new AbortController() }
            : t,
        ),
      }));
      pump();
    },
    discardInterrupted: (entry) => {
      forgetPendingUpload(entry.fingerprint);
      void api.delete(`/files/uploads/${entry.sessionId}`).catch(() => undefined);
    },
    dismiss: () => {
      for (const task of get().tasks) {
        if (task.status === 'uploading' || task.status === 'saving' || task.status === 'queued')
          task.controller.abort();
      }
      set({ tasks: [], batches: [], open: false, collapsed: false, interruptedHidden: true });
    },
    setOpen: (open) => set({ open }),
    setCollapsed: (collapsed) => set({ collapsed }),
  };
});

/** Totals of the upload panel (pure). */
export function uploadSummary(tasks: readonly DiskUploadTask[]) {
  const active = tasks.filter(
    (t) => t.status === 'queued' || t.status === 'uploading' || t.status === 'saving',
  );
  const failed = tasks.filter((t) => t.status === 'error').length;
  const done = tasks.filter((t) => t.status === 'done').length;
  const bytes = tasks.reduce((sum, t) => sum + t.file.size, 0);
  const sent = tasks.reduce(
    (sum, t) =>
      sum + (t.status === 'done' || t.status === 'saving' ? t.file.size : (t.file.size * t.progress) / 100),
    0,
  );
  return {
    active: active.length,
    failed,
    done,
    total: tasks.length,
    percent: bytes > 0 ? Math.round((sent / bytes) * 100) : tasks.length > 0 && active.length === 0 ? 100 : 0,
  };
}
